import { Capacitor, registerPlugin } from "@capacitor/core";

/**
 * Whether this install may request LIVE ads — the single gate every ad call
 * goes through. Policy and history: docs/ads-policy.md (AdMob suspended
 * pub-7082278595197706 for invalid traffic on 2026-10-23).
 *
 *   • "off"  — no ad request at all: plain browser, Firebase Test Lab / Play
 *              pre-launch robots, Android TV (decided natively, see
 *              AdsEnvPlugin.java).
 *   • "test" — Google's test creatives only: debug native builds, `vite dev`,
 *              VITE_ADMOB_TEST_MODE=true, and any native shell without the
 *              AdsEnv plugin (iOS today). Unknown means test, never live.
 *   • "live" — a signed Android release build on a phone/tablet.
 */
export type AdsMode = "live" | "test" | "off";

interface AdsEnvPlugin {
  get(): Promise<{ mode: AdsMode }>;
}

const AdsEnv = registerPlugin<AdsEnvPlugin>("AdsEnv");

/**
 * Hashed device ids (logcat prints them: "Use RequestConfiguration.Builder()
 * .setTestDeviceIds(Arrays.asList("…"))") of every phone the team installs a
 * RELEASE build on — those get test ads even from a Play Store install.
 * Prefer registering the device in AdMob → Settings → Test devices, which
 * covers every app on the account; this list is the in-code backup.
 */
export const ADMOB_TEST_DEVICE_IDS: string[] = [];

let modePromise: Promise<AdsMode> | null = null;

export function adsMode(): Promise<AdsMode> {
  modePromise ??= resolveMode();
  return modePromise;
}

async function resolveMode(): Promise<AdsMode> {
  if (!Capacitor.isNativePlatform()) return "off";
  let native: AdsMode = "test";
  if (Capacitor.isPluginAvailable("AdsEnv")) {
    try {
      native = (await AdsEnv.get()).mode;
    } catch {
      native = "test";
    }
  }
  if (native === "off") return "off";
  if (native !== "live") return "test";
  if (import.meta.env.DEV || import.meta.env.VITE_ADMOB_TEST_MODE === "true") return "test";
  return "live";
}
